import React, { useState } from "react";
import { Container, Row, Col, Card, Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import logo from "../assets/images/logo.png";
import waterscape from "../assets/images/waterscape.webp";
import "./opportunities.scss";

export const Opportunities = () => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const jobOpenings = [
    {
      id: "embedded-firmware-developer",
      title: "Embedded Firmware Developer",
      department: "Engineering",
      location: "Remote • On-site • Hybrid",
      type: "Contract • Contract-to-Hire • Full-time",
      summary: "Develop and maintain ESP32-S3 firmware for our sophisticated fishing electronics with real-time networking, React web interface, and marine integration capabilities.",
      requirements: ["<strong>Expertise in ESP32 Firmware Development:</strong> Strong proficiency in embedded C/C++ and ESP-IDF framework, with hands-on experience developing and optimizing firmware for ESP32-S3 microcontrollers, including FreeRTOS and peripheral integration (PWM, ADC, GPIO).", "<strong>Networking and IoT Proficiency:</strong> In-depth knowledge of networking protocols (TCP/IP, HTTP/HTTPS, WebSockets, WiFi, BLE) for real-time communication in Accutroll devices, with experience in IoT system integration and RESTful API design.", "<strong>Full-Stack Embedded-Web Skills:</strong> Proven ability to develop and debug React/TypeScript-based web interfaces embedded in firmware, ensuring seamless integration with Accutroll's real-time control systems for fishing and trolling applications."]
    },
    {
      id: "senior-flutter-developer",
      title: "Senior Flutter App Developer",
      department: "Engineering",
      location: "Remote • On-site • Hybrid",
      type: "Contract • Contract-to-Hire • Full-time",
      summary: "Develop and maintain our sophisticated Flutter mobile application for managing marine/fishing IoT devices.",
      requirements: ["<strong>Expert Flutter & Dart Proficiency:</strong> Extensive experience in Flutter development with advanced knowledge of Dart, including BLoC state management and complex asynchronous operations.", "<strong>IoT Device Management Expertise:</strong> Proven skills in managing IoT devices, with experience in WiFi networking, mDNS/Bonjour service discovery, and Bluetooth Low Energy (BLE) for seamless Accutroll device integration.", "<strong>Cross-Platform Optimization:</strong> Strong ability to optimize mobile applications for real-time performance and ensure compatibility across Android and iOS platforms, including native integrations and Drift SQLite ORM usage."]
    }
  ];

  return (
    <div className="opportunities-page">
      <Container>
        <Row className="opportunities-header">
          <Col>
            <Link to="/" className="logo-link">
              <img src={logo} alt="Innotech Marine" width={200} className="mb-4" />
            </Link>
            <h1>Join Our Team</h1>
            <p className="lead">
            </p>
          </Col>
        </Row>

        <Row className="waterscape-section mb-5">
          <Col>
            <div className="waterscape-container">
              <img src={waterscape} alt="Pacific Northwest Waterscape" className="waterscape-image" />
              <div className="waterscape-overlay">
                <p>Be part of our mission to revolutionize marine electronics and precision fishing systems, working from our picturesque Bainbridge Island headquarters in Washington's Pacific Northwest—just a scenic 35-minute ferry ride from Seattle.</p>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="company-info">
          <Col md={6}>
            <h3>Why work with us?</h3>
            <div className="benefits-list">
              <div className="benefit-item">
                <strong>Innovate Fishing Electronics:</strong> Drive the future of fishing technology with IoT-powered solutions like <a href="https://accutroll.com" target="_blank" rel="noopener noreferrer" className="accutroll-link">Accutroll</a>, creating smarter, connected tools for anglers.
              </div>
              <div className="benefit-item">
                <strong>Collaborative Team Environment:</strong> Work closely with a passionate team dedicated to advancing fishing electronics through innovative design.
              </div>
              <div className="benefit-item">
                <strong>Expand Your Technical Skills:</strong> Build expertise in IoT and fishing-focused systems, tackling hands-on projects that redefine the industry.
              </div>
              <div className="benefit-item">
                <strong>Flexible Work Options:</strong> Choose from remote, hybrid, or on-site arrangements to suit your lifestyle.
              </div>
            </div>
          </Col>
          <Col md={6}>
            <h3>Our Mission</h3>
            <p>
              We're committed to revolutionizing marine electronics with intelligent, 
              precision control systems that enhance the fishing and boating experience. 
              Our products like Accutroll represent the cutting edge of marine technology innovation.
            </p>
            <div className="employment-note mt-3">
              <strong>Open to All Arrangements:</strong> We welcome contractors, 
              contract-to-hire, and full-time candidates who resonate with our mission.
            </div>
          </Col>
        </Row>

        <Row className="job-listings">
          <Col>
            <h2 className="mb-4">Current Openings</h2>
            {jobOpenings.map((job) => (
              <Card key={job.id} className="job-card mb-4">
                <Card.Body>
                  <div className="job-header">
                    <div>
                      <Card.Title>{job.title}</Card.Title>
                      <div className="job-meta">
                        <span className="department">{job.department}</span>
                        <span className="separator">•</span>
                        <span className="location">{job.location}</span>
                        <span className="separator">•</span>
                        <span className="type">{job.type}</span>
                      </div>
                    </div>
                    <Button as={Link} to={`/opportunities/${job.id}`} variant="primary">
                      View Details
                    </Button>
                  </div>
                  <Card.Text className="job-summary">
                    {job.summary}
                  </Card.Text>
                  <div className="key-requirements">
                    <strong>Key Requirements:</strong>
                    <ul>
                      {job.requirements.map((req, index) => (
                        <li key={index} dangerouslySetInnerHTML={{ __html: req }}></li>
                      ))}
                    </ul>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </Col>
        </Row>

        <Row className="contact-section">
          <Col className="text-center">
            <h3>Don't See Your Role?</h3>
            <p>
              We're always interested in talking to talented individuals who resonate with our mission. 
              Whether you're looking for contract work, contract-to-hire opportunities, or full-time employment,
              send us your resume and let us know how you'd like to contribute.
            </p>
            <Button 
              variant="outline-primary" 
              size="lg"
              onClick={() => setShowEmailModal(true)}
            >
              Get In Touch
            </Button>
          </Col>
        </Row>
      </Container>

      {/* Email Contact Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Contact Us</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <h5>Email us at:</h5>
          <div className="email-display">
            <a href="mailto:opportunities@innotechmarine.co" className="email-link">
              opportunities@innotechmarine.co
            </a>
          </div>
          <p className="mt-3 text-muted">
            We'll get back to you as soon as possible!
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
};
